import {
  Button,
  ButtonGroup,
  CloseButton,
  Col,
  Form,
  Modal,
  Row,
} from "react-bootstrap";
import {
  useGetAllSongsQuery,
  useGetAllLanguagesQuery,
  useGetAllTagsQuery,
  useGetAllCategoriesQuery,
  useUpdateSongMutation,
  useAddSongMutation,
  useGetAllSongContactsQuery,
} from "../../slices/songsApiSlice";
import { useEffect, useState } from "react";
import Select from "react-select";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import "@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css";
import "react-calendar/dist/Calendar.css";
import { toast } from "react-toastify";

const Songs = () => {
  const [selectedSong, setSelectedSong] = useState(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const [formData, setFormData] = useState({
    id: null,
    artist: "",
    title: "",
    language: "",
    paid: true,
    code: "",
    playDates: [],
    duration: 0,
    category: [],
    tags: [],
    notifications: [],
  });

  const {
    data: songs,
    error: songsError,
    isLoading: songsLoading,
  } = useGetAllSongsQuery();

  const { data: contacts } = useGetAllSongContactsQuery();
  const { data: languages } = useGetAllLanguagesQuery();
  const { data: categories } = useGetAllCategoriesQuery();
  const { data: tags } = useGetAllTagsQuery();

  const [updateSong] = useUpdateSongMutation();
  const [addSong] = useAddSongMutation();

  useEffect(() => {
    if (songs) {
      if (selectedSong === null) {
        setSelectedSong(songs[0]._id);
      }
    }
    // eslint-disable-next-line
  }, [songs]);


  useEffect(() => {
    if (selectedSong != null) {
      const selectedSongData = songs.find((song) => song._id === selectedSong);
      if (selectedSongData !== undefined) {
        setFormData({
          ...formData,
          id: selectedSong,
          artist: selectedSongData.artist,
          title: selectedSongData.title,
          language: selectedSongData.language,
          paid: selectedSongData.paid,
          code: selectedSongData.code,
          playDates: selectedSongData.playDates,
          duration: selectedSongData.duration,
          category: selectedSongData.category,
          tags: selectedSongData.tags,
          notifications: selectedSongData.notifications,
        });
      }
    }
    // eslint-disable-next-line
  }, [selectedSong, songs]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.artist ||
      !formData.title ||
      !formData.language ||
      !formData.code ||
      !formData.playDates ||
      !formData.duration ||
      !formData.category ||
      !formData.tags
    ) {
      toast.error("All fields are required. Document not saved.", {
        position: "bottom-center",
        autoClose: 2500,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
        theme: "colored",
      });
      return;
    }
    if (formData.id !== null) {
      await updateSong(formData);
    } else {
      const newSong = await addSong(formData);
      setSelectedSong(newSong.data._id);
    }
    setIsFormDirty(false);
  };

  const handleCheckboxChange = (e) => {
    const { id, checked } = e.target;
    if (checked) {
      setFormData((prevData) => ({
        ...prevData,
        [id]: true,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [id]: false,
      }));
    }
    setIsFormDirty(true);
  };

  const handlePlayDateAMPMChange = (e, period, index) => {
    e.preventDefault();
    const { checked } = e.target;
    if (checked) {
      setFormData((prevData) => ({
        ...prevData,
        playDates: prevData.playDates.map((playDate, i) =>
          i === index ? { ...playDate, [period]: true } : playDate
        ),
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        playDates: prevData.playDates.map((playDate, i) =>
          i === index ? { ...playDate, [period]: false } : playDate
        ),
      }));
    }
    setIsFormDirty(true);
  };

  const handlePlayDateChange = (e, index) => {
    const startDate = new Date(e[0]).toISOString();
    const endDate = new Date(e[1]).toISOString();
    setFormData((prevData) => ({
      ...prevData,
      playDates: prevData.playDates.map((playDate, i) =>
        i === index ? { ...playDate, start: startDate, end: endDate } : playDate
      ),
    }));
    setIsFormDirty(true);
  };

  const handleFormChange = (e) => {
    e.preventDefault();
    const { id, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
    setIsFormDirty(true);
  };

  const handleCategoriesChange = (e, fieldName) => {
    const { name } = fieldName;
    setFormData((prevData) => ({
      ...prevData,
      [name]: e,
    }));
    setIsFormDirty(true);
  };

  const handleAddDateButtonClick = (programId) => {
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);
    startDate.setDate(startDate.getDate() + 1);

    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    endDate.setDate(endDate.getDate() + 29);

    setFormData((prevData) => ({
      ...prevData,
      playDates: [
        ...prevData.playDates,
        {
          start: startDate.toISOString(),
          end: endDate.toISOString(),
          am: false,
          pm: true,
        },
      ],
    }));
    setIsFormDirty(true);
  };

  const handleAddSong = (e) => {
    e.preventDefault();
    setFormData({
      id: null,
      artist: "",
      title: "",
      language: "",
      paid: false,
      code: "",
      playDates: [],
      duration: 0,
      category: [],
      tags: [],
      notifications: [],
    });
    setIsFormDirty(true);
  };

  const handleDeleteNotification = async (e) => {
    e.preventDefault();
    try {
      const confirmed = window.confirm(
        "Are you sure you want to delete this notification?"
      );
      if (confirmed) {
        const updatedNotifications = formData.notifications.filter(
          (notification) => notification.contact._id !== e.target.id
        );
        setFormData({ ...formData, notifications: updatedNotifications });
        setIsFormDirty(true);
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };
  const handleAddNotification = async (e) => {
    e.preventDefault();
    setSelectedContact(contacts[0]._id);
    setShowContactModal(true);
  };

  const handleSaveContact = async (e) => {
    e.preventDefault();

    const selectedContact = contacts.find(
      (contact) => contact._id === e.target.id
    );

    setFormData((prevFormData) => ({
      ...prevFormData,
      notifications: [
        ...prevFormData.notifications.filter(
          (notification) => Object.keys(notification).length !== 0
        ),
        {
          contact: selectedContact,
        },
      ],
    }));

    setIsFormDirty(true);
    setShowContactModal(false);
  };

  const datePlayDates = (fromdate, todate, index) => {
    const playDates = [new Date(fromdate), new Date(todate)];
    return (
      <DateRangePicker
        onChange={(e) => handlePlayDateChange(e, index)}
        value={playDates}
        format="dd-MM-yyyy"
      />
    );
  };

  return (
    <>
      <Row className="mt-4">
        <Col
          xs="6"
          style={{ height: "1000px", overflowY: "scroll" }}
          className="p-2 border border-1"
        >
          {songsError ? (
            <div>Error loading Songs</div>
          ) : songsLoading ? (
            <div>Loading Songs...</div>
          ) : (
            <ButtonGroup
              vertical
              size="sm"
              className="col-12"
            >
              {songs &&
                songs.map((song) => (
                  <Button
                    key={song._id}
                    variant={selectedSong === song._id ? "primary" : "light"}
                    onClick={() => {
                      setSelectedSong(song._id);
                      setIsFormDirty(false);
                    }}
                  >
                    <Row>
                      <Col
                        className="text-start text-capitalize text-truncate"
                        xs="3"
                      >
                        {song.artist}
                      </Col>
                      <Col
                        className="text-start text-capitalize overflow-x-hidden text-truncate"
                        xs="6"
                      >
                        {song.title}
                      </Col>
                      <Col
                        className="text-sm-end"
                        xs="3"
                      >
                        {song.paid ? (
                          <span className="badge bg-danger p-2 mx-2  "> </span>
                        ) : (
                          <span className="badge p-2 mx-2  "> </span>
                        )}
                        {song.current ? (
                          <span className="badge bg-success p-2 mx-2  "> </span>
                        ) : (
                          <span className="badge p-2 mx-2  "> </span>
                        )}
                      </Col>
                    </Row>
                  </Button>
                ))}
            </ButtonGroup>
          )}
        </Col>
        <Col className="p-2 border border-1 border-start-0">
          <small>
            <Row>
              <Col>
                <Form onSubmit={handleSubmit}>
                  <Row className="h4 border-bottom pb-3 ">
                    <Col className="text-center">Song</Col>
                  </Row>
                  <Row className="align-items-center pt-2 ">
                    <Col
                      sm={2}
                      className="align-self-center text-end"
                    >
                      Artist:
                    </Col>
                    <Col>
                      <Form.Group controlId="artist">
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="Enter Artist"
                          value={formData.artist}
                          onChange={handleFormChange}
                          maxLength={40}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="align-items-center pt-2 ">
                    <Col
                      sm={2}
                      className="align-self-center text-end"
                    >
                      Title:
                    </Col>
                    <Col>
                      <Form.Group controlId="title">
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="Enter Song Title"
                          value={formData.title}
                          onChange={handleFormChange}
                          maxLength={40}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="align-items-center pt-2 ">
                    <Col
                      sm={2}
                      className="align-self-center text-end"
                    >
                      Code:
                    </Col>
                    <Col>
                      <Form.Group controlId="code">
                        <Form.Control
                          size="sm"
                          as="textarea"
                          placeholder="Enter Code"
                          value={formData.code}
                          onChange={handleFormChange}
                          rows={3}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="pt-2 mt-2 align-items-center ">
                    <Col
                      className="text-end  pt-2 "
                      xs={2}
                    >
                      Language:
                    </Col>
                    <Col>
                      <Row className="align-items-center ">
                        <Col>
                          <Form.Group controlId="language">
                            <Form.Control
                              size="sm"
                              as="select"
                              value={formData.language}
                              onChange={handleFormChange}
                            >
                              <option value="">Select Language</option>
                              {languages &&
                                languages.map((language) => (
                                  <option
                                    key={language._id}
                                    value={language._id}
                                  >
                                    {language.language}
                                  </option>
                                ))}
                            </Form.Control>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row className="pt-2 align-items-center">
                    <Col
                      className="text-end  pt-2 "
                      xs={2}
                    >
                      Paid:
                    </Col>
                    <Col
                      xs={3}
                      className="align-items-center"
                    >
                      <Row>
                        <Col xs={9}>
                          <Form.Check // prettier-ignore
                            type={"checkbox"}
                            isValid
                            id={"paid"}
                            checked={formData.paid ? true : false}
                            defaultValue={formData.paid}
                            onChange={handleCheckboxChange}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row className="pt-2 align-items-center">
                    <Col
                      sm={2}
                      className="text-end pt-2 "
                    >
                      Length Secs:
                    </Col>
                    <Col
                      sm={3}
                      className="pt-2"
                    >
                      <Form.Group>
                        <Form.Control
                          id={"duration"}
                          size="sm"
                          type="text"
                          placeholder="Enter run time"
                          value={formData.duration}
                          onChange={handleFormChange}
                          maxLength={3}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="pt-2 mt-2 align-items-center">
                    <Col
                      className="text-end  pt-2 "
                      xs={2}
                    >
                      Categories:
                    </Col>
                    <Col>
                      <Row className="align-items-center ">
                        <Col>
                          <Select
                            name="category"
                            size="sm"
                            isMulti
                            closeMenuOnSelect={false}
                            options={categories}
                            value={formData.category}
                            onChange={handleCategoriesChange}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row className="pt-2 mt-2 align-items-center">
                    <Col
                      className="text-end  pt-2 "
                      xs={2}
                    >
                      Tags:
                    </Col>
                    <Col>
                      <Row className="align-items-center ">
                        <Col>
                          <Select
                            name="tags"
                            size="sm"
                            isMulti
                            closeMenuOnSelect={false}
                            options={tags}
                            value={formData.tags}
                            onChange={handleCategoriesChange}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row className="pt-2 mt-2">
                    <Col
                      className="text-end  pt-2 "
                      xs={2}
                    >
                      Play Dates:
                    </Col>
                    <Col>
                      {/* Map through playdates */}
                      {formData.playDates.map((playdate, index) => (
                        <Row
                          key={index}
                          className="pt-1"
                        >
                          <Col xs={6}>
                            {datePlayDates(playdate.start, playdate.end, index)}
                          </Col>
                          <Col xs={1}>am:</Col>
                          <Col xs={1}>
                            <Form.Check // prettier-ignore
                              type={"checkbox"}
                              isValid
                              checked={playdate.am ? true : false}
                              defaultValue={playdate.am}
                              onChange={(e) =>
                                handlePlayDateAMPMChange(e, "am", index)
                              }
                            />
                          </Col>
                          <Col xs={1}>pm:</Col>
                          <Col xs={1}>
                            <Form.Check // prettier-ignore
                              type={"checkbox"}
                              isValid
                              checked={playdate.pm ? true : false}
                              defaultValue={playdate.pm}
                              onChange={(e) =>
                                handlePlayDateAMPMChange(e, "pm", index)
                              }
                            />
                          </Col>
                        </Row>
                      ))}
                      <Row className="pt-2">
                        <Col
                          // xs={10}
                          className="text-end me-5"
                        >
                          <Button
                            size="sm"
                            variant="outline-info"
                            onClick={handleAddDateButtonClick}
                          >
                            Add Date
                          </Button>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row className="pt-2 mt-2">
                    <Col
                      className="text-end  pt-2 "
                      xs={2}
                    >
                      Notifications:
                    </Col>
                    <Col>
                      {formData.notifications &&
                        formData.notifications.length > 0 &&
                        formData.notifications.map((notification, index) => (
                          <Row
                            key={index}
                            className="mt-2"
                          >
                            {notification.contact &&
                            notification.contact.firstName ? (
                              <>
                                <Col xs={6}>
                                  {notification.contact.firstName}{" "}
                                  {notification.contact.surname}
                                </Col>
                                <Col>
                                  <CloseButton
                                    id={notification.contact._id}
                                    onClick={(e) => handleDeleteNotification(e)}
                                  />
                                </Col>
                              </>
                            ) : null}
                          </Row>
                        ))}
                    </Col>
                  </Row>
                  <Row>
                    <Col
                      className="text-end me-5"
                      // xs={12}
                    >
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={(e) => handleAddNotification(e)}
                        // disabled={isFormDirty}
                        // onClick={handleAddSong}
                      >
                        Add a notification
                      </Button>
                    </Col>
                  </Row>
                  <Row className="p-2 mt-4 text-center  align-items-center">
                    <Col>
                      <Button
                        variant="danger"
                        disabled={isFormDirty}
                        onClick={handleAddSong}
                      >
                        Add A Song
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        variant="primary"
                        disabled={!isFormDirty}
                        type="submit"
                      >
                        Save Changes
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Col>
            </Row>
          </small>
        </Col>
      </Row>
      <Modal
        show={showContactModal}
        onHide={() => {
          setShowContactModal(false);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Selet a Contact</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Select
            size="sm"
            id="epgInput"
            onChange={(e) => {
              setSelectedContact(e.target.value);
            }}
          >
            {contacts &&
              contacts.map((contact) => (
                <option
                  key={contact._id}
                  value={contact._id}
                >
                  {contact.firstName} {contact.surname}
                </option>
              ))}
          </Form.Select>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowContactModal(false);
            }}
          >
            Close
          </Button>
          <Button
            id={selectedContact}
            variant="danger"
            onClick={handleSaveContact}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default Songs;
